"use client";

import cn from "classnames";

import { ChevronRightIcon } from "@/components/icons/chevronRight.icon";
import { MonitoringPlanSlotTitle } from "@/components/monitoringPlanSlotTitle/monitoringPlanSlotTitle.component";
import { Price } from "@/components/price/price.component";
import { QueryLink } from "@/components/queryLink/queryLink.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import {
  MONITORING_SYSTEM_DRAWER_DEFAULT_SLUG,
  MONITORING_SYSTEM_DRAWER_SEARCH_PARAM,
} from "@/app/blu/monitoring/constants";
import type { GetMonitoringPageQueryResult } from "@/sanity.types";

export function MonitoringPlanCard({
  plan,
  defaultSystemDrawerProductId,
  fullWidth = false,
  theme = "tertiary",
}: MonitoringPlanCardProps) {
  const isRecommended = plan.isRecommended === true;
  const systemDrawerProductId =
    plan.productId ||
    defaultSystemDrawerProductId ||
    MONITORING_SYSTEM_DRAWER_DEFAULT_SLUG;
  const showSystemDrawerLink = Boolean(
    plan?.labelAbovePlanTitle && plan.labelOpensSystemDrawer,
  );

  return (
    <article
      className={cn(
        "flex h-auto flex-col overflow-hidden rounded-xl",
        fullWidth ? "w-full min-w-0 flex-1" : "max-w-lg bg-white/50",
        fullWidth && cardThemeBackground(theme),
        {
          "border-secondary order-first border-2 md:order-none": isRecommended,
          "sm:pt-10": !isRecommended,
          "border border-white": !fullWidth && !isRecommended,
        },
      )}>
      {isRecommended && (
        <div className="bg-secondary relative px-2 py-2 sm:px-3">
          <Text
            variant="preHeaderLg"
            component="h4"
            className="text-center text-white uppercase">
            Recommended
          </Text>
        </div>
      )}

      <div
        className={cn(
          "flex flex-1 flex-col gap-2 px-4 py-5 sm:px-5 md:px-6 md:py-6",
          { "sm:pt-8": isRecommended },
        )}>
        <div className="flex flex-col gap-3">
          {showSystemDrawerLink ? (
            <QueryLink
              set={{
                [MONITORING_SYSTEM_DRAWER_SEARCH_PARAM]: systemDrawerProductId,
              }}
              className="mx-auto block flex max-w-prose cursor-pointer items-center gap-1 underline-offset-2 hover:underline">
              <Text
                variant="body4"
                className="text-greyscale-body text-center underline">
                {plan.labelAbovePlanTitle}
              </Text>
              <ChevronRightIcon
                width={16}
                height={16}
                className="mb-0.5 shrink-0"
              />
            </QueryLink>
          ) : plan?.labelAbovePlanTitle ? (
            <Text
              variant="body4"
              component="p"
              className="text-greyscale-body mx-auto max-w-prose text-center">
              {plan.labelAbovePlanTitle}
            </Text>
          ) : null}

          <MonitoringPlanSlotTitle
            title={plan.title}
            titleBold={plan.titleBold}
            titleRegular={plan.titleRegular}
            variant="h5"
            component="h3"
            className="text-greyscale-headline text-center !text-[32px]"
          />
          {plan?.longDescription ? (
            <div className="text-greyscale-body [&_p]:text-greyscale-body mx-auto max-w-prose text-sm leading-relaxed sm:text-base">
              <RichText>{plan.longDescription}</RichText>
            </div>
          ) : plan?.shortDescription ? (
            <Text
              variant="body1"
              component="span"
              className="text-greyscale-body text-sm leading-relaxed sm:text-base">
              {plan.shortDescription}
            </Text>
          ) : null}
        </div>

        <div className="mt-auto">
          {/* Divider between what's included and price */}
          <hr className="border-greyscale-strength/20 my-4 border-t opacity-20" />

          <div
            className={cn(
              "flex w-full min-w-0 flex-wrap items-end gap-3",
              { "justify-between": plan?.legal },
              { "justify-end": !plan?.legal },
            )}>
            {plan?.legal ? (
              <div className="text-greyscale-body max-w-[min(100%,28rem)] min-w-0 flex-1 text-left text-sm leading-relaxed">
                <RichText>{plan.legal}</RichText>
              </div>
            ) : null}
            <Price
              value={plan.priceAmount}
              monthly={plan.priceFrequency === "monthly"}
              primary
              className={cn(
                "text-greyscale-headline shrink-0 text-right text-lg font-bold md:text-xl",
                { "ml-auto": !plan?.legal },
              )}
            />
          </div>
        </div>
      </div>
    </article>
  );
}

type MonitoringPlanCardProps = {
  plan: MonitoringPlanCardData;
  defaultSystemDrawerProductId?: string | null;
  fullWidth?: boolean;
  theme?: string | null;
};

const CARD_THEME_BACKGROUND: Record<string, string> = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  tertiary: "bg-primary-100",
  light: "bg-greyscale-background",
  white: "bg-white",
  success: "bg-success",
  error: "bg-error",
};

function cardThemeBackground(theme?: string | null) {
  return (
    CARD_THEME_BACKGROUND[theme || "tertiary"] ?? CARD_THEME_BACKGROUND.tertiary
  );
}

type MonitoringPageData = NonNullable<GetMonitoringPageQueryResult>;
type MonitoringPlansData = NonNullable<MonitoringPageData["monitoringPlans"]>;
export type MonitoringPlanCardData = NonNullable<
  MonitoringPlansData["plansWithCameras"]
>[number];
